<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useAciSipConnection } from 'dashboard/api/telephony_aci/useAciSipConnection';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const props = defineProps({
  phone: { type: String, default: '' },
  contactId: { type: [String, Number], default: null },
  contactName: { type: String, default: null },
  contactThumbnail: { type: String, default: null },
  conversationId: { type: [String, Number], default: null },
  inboxId: { type: [String, Number], default: null },
  label: { type: String, default: '' },
  icon: { type: [String, Object, Function], default: 'i-lucide-phone' },
  size: { type: String, default: 'sm' },
  tooltipLabel: { type: String, default: '' },
  alwaysShow: { type: Boolean, default: true },
});

const { t } = useI18n();
const store = useStore();

// Obter usuario atual
const getCurrentUser = useMapGetter('getCurrentUser');
const currentUser = computed(() => getCurrentUser.value);
const getCurrentAccountId = useMapGetter('getCurrentAccountId');
const accountId = computed(() => getCurrentAccountId.value);

// DIDs para descobrir o DDD local
const didRecords = useMapGetter('asteriskDids/getRecords');
const defaultDid = computed(() => {
  const records = didRecords.value || [];
  const active = records.filter(d => d.active === 1 || d.active === true);
  return active[0] || records[0] || null;
});
const localDDD = computed(() => {
  if (!defaultDid.value?.did_number) return null;
  const clean = String(defaultDid.value.did_number)
    .replace(/\D/g, '')
    .replace(/^55/, '');
  return clean.slice(0, 2);
});

// Formata o numero para discagem:
// - Se mesmo DDD do DID (local): passa so o celular (9 digitos com 9 no inicio)
// - Se DDD diferente: passa com DDD (adicionando o 9 se necessario)
// Remove codigo do pais (55) sempre
const formatPhoneForDialing = phone => {
  if (!phone) return phone;
  let clean = String(phone).replace(/\D/g, '').replace(/^55/, '');
  if (!clean) return phone;

  const addNinthDigit = num => {
    // Celular sem 9: 8 digitos comecando com 6-9
    if (num.length === 8 && /^[6-9]/.test(num)) {
      return `9${num}`;
    }
    return num;
  };

  if (localDDD.value && clean.length >= 10) {
    const numberDDD = clean.slice(0, 2);
    if (numberDDD === localDDD.value) {
      return addNinthDigit(clean.slice(2));
    }
    // DDD diferente: mantem DDD e garante o 9 no celular
    return numberDDD + addNinthDigit(clean.slice(2));
  }

  return clean;
};

// Check if telephony feature is enabled for this account
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const isTelephonyEnabled = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.ASTERISK_TELEPHONY
  );
});

// SIP Connection via composable
const {
  isConnected,
  isConnecting,
  callState,
  callNumber,
  hasActiveCall,
  connect,
  makeCall: sipMakeCall,
  hangup,
  setCallMetadata,
} = useAciSipConnection();

// Loading states para evitar clique duplo
const isProcessing = ref(false);

// Verifica se a chamada atual e para este numero
const isCallForThisNumber = computed(() => {
  if (!callNumber.value || !props.phone) return false;
  const normalizeNumber = num => num.replace(/\D/g, '').slice(-9);
  return normalizeNumber(callNumber.value) === normalizeNumber(props.phone);
});

// Mostrar botao se telefonia habilitada E tem telefone E (esta conectado OU alwaysShow=true)
const shouldRender = computed(() => {
  if (!isTelephonyEnabled.value) return false;
  if (!props.phone) return false;
  return props.alwaysShow || isConnected.value;
});

// Status visual simplificado
const visualStatus = computed(() => {
  if (isProcessing.value || isConnecting.value) return 'processing';
  if (!isConnected.value) return 'disconnected';

  if (isCallForThisNumber.value) {
    if (callState.value === 'connected') return 'in-call';
    if (['calling', 'ringing', 'connecting'].includes(callState.value))
      return 'calling';
  }

  if (hasActiveCall.value) return 'blocked';

  return 'ready';
});

// Tooltip dinamico baseado no status
const dynamicTooltip = computed(() => {
  if (props.tooltipLabel) return props.tooltipLabel;

  switch (visualStatus.value) {
    case 'processing':
      return 'Processando...';
    case 'disconnected':
      return 'Telefonia desconectada - Clique para conectar';
    case 'calling':
      return 'Chamando... Clique para cancelar';
    case 'in-call':
      return 'Em chamada - Clique para desligar';
    case 'blocked':
      return 'Ja existe uma chamada em andamento';
    default:
      return `Ligar para ${props.phone}`;
  }
});

// Icone dinamico baseado no status
const dynamicIcon = computed(() => {
  switch (visualStatus.value) {
    case 'processing':
      return null;
    case 'calling':
      return 'i-lucide-phone-outgoing';
    case 'in-call':
      return 'i-lucide-phone-off';
    default:
      return props.icon;
  }
});

// Desabilitar botao
const isDisabled = computed(() => {
  if (isProcessing.value) return true;
  if (visualStatus.value === 'blocked') return true;
  return false;
});

// Handler principal com protecao contra clique duplo
const handleClick = async () => {
  if (isProcessing.value) return;

  if (isCallForThisNumber.value && hasActiveCall.value) {
    await handleHangup();
    return;
  }

  if (hasActiveCall.value) {
    useAlert('Ja existe uma chamada em andamento');
    return;
  }

  if (!isConnected.value) {
    await handleConnect();
    return;
  }

  await handleMakeCall();
};

// Conectar ao SIP
const handleConnect = async () => {
  isProcessing.value = true;

  try {
    const success = await connect();

    if (success) {
      useAlert('Telefonia conectada!');
    } else {
      useAlert('Nao foi possivel conectar');
    }
  } catch (error) {
    console.error('[VoiceCallButton] Erro ao conectar:', error);
    useAlert('Erro ao conectar telefonia');
  } finally {
    isProcessing.value = false;
  }
};

// Fazer chamada
const handleMakeCall = async () => {
  if (!props.phone) {
    useAlert(t('CONTACT_PANEL.NO_PHONE_NUMBER'));
    return;
  }

  isProcessing.value = true;

  try {
    const extraHeaders = {};

    if (props.contactId) {
      extraHeaders['X-Contact-ID'] = props.contactId;
    }
    if (props.conversationId) {
      extraHeaders['X-Conversation-ID'] = props.conversationId;
    }
    if (props.inboxId) {
      extraHeaders['X-Inbox-ID'] = props.inboxId;
    }
    if (accountId.value) {
      extraHeaders['X-Account-ID'] = accountId.value;
    }
    if (currentUser.value?.id) {
      extraHeaders['X-User-ID'] = currentUser.value.id;
    }

    // Define metadados do contato para o historico
    if (props.contactName || props.contactThumbnail) {
      setCallMetadata({
        name: props.contactName,
        thumbnail: props.contactThumbnail,
      });
    }

    const phoneToDial = formatPhoneForDialing(props.phone);

    sipMakeCall(
      phoneToDial,
      event => {
        if (event.type === 'failed') {
          useAlert('Chamada falhou');
        }
      },
      extraHeaders
    );

    // Abre o DialerPanel para controles da chamada
    emitter.emit(BUS_EVENTS.TELEPHONY_SHOW_DIALER);
  } catch (error) {
    console.error('[VoiceCallButton] Erro:', error);
    useAlert(error.message || t('CONTACT_PANEL.CALL_FAILED'));
  } finally {
    setTimeout(() => {
      isProcessing.value = false;
    }, 300);
  }
};

// Desligar chamada
const handleHangup = async () => {
  isProcessing.value = true;

  try {
    hangup();
  } catch (error) {
    console.error('[VoiceCallButton] Erro ao desligar:', error);
    useAlert('Erro ao desligar');
  } finally {
    setTimeout(() => {
      isProcessing.value = false;
    }, 500);
  }
};

// Auto-conectar ao montar se telefonia habilitada e nao estiver conectado
onMounted(async () => {
  // Only connect if telephony feature is enabled
  if (!isTelephonyEnabled.value) {
    return;
  }

  // Carrega DIDs para descobrir o DDD local (se ainda nao carregados)
  if (!didRecords.value || didRecords.value.length === 0) {
    store.dispatch('asteriskDids/fetch').catch(() => {});
  }

  if (!isConnected.value && !isConnecting.value) {
    await connect();
  }
});
</script>

<template>
  <span v-if="shouldRender" class="contents">
    <button
      v-tooltip.top-end="dynamicTooltip"
      type="button"
      class="relative inline-flex items-center justify-center rounded-lg transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-slate-8"
      :class="[
        // Tamanhos
        {
          'h-6 w-6': size === 'xs',
          'h-8 w-8': size === 'sm',
          'h-10 w-10': size === 'md',
          'h-12 w-12': size === 'lg',
        },
        // Cursor
        {
          'cursor-wait': isProcessing,
          'cursor-not-allowed': isDisabled && !isProcessing,
          'cursor-pointer': !isDisabled && !isProcessing,
        },
      ]"
      :disabled="isDisabled"
      @click="handleClick"
    >
      <!-- Background com cores baseadas no status -->
      <span
        class="absolute inset-0 rounded-lg transition-colors"
        :class="{
          // Processing - amber
          'bg-n-amber-3 dark:bg-n-amber-9/20': visualStatus === 'processing',
          // Disconnected - ruby
          'bg-n-ruby-3 dark:bg-n-ruby-9/20': visualStatus === 'disconnected',
          // Calling - amber
          'bg-n-amber-3 dark:bg-n-amber-9/20': visualStatus === 'calling',
          // In call - teal solido
          'bg-n-teal-9': visualStatus === 'in-call',
          // Blocked - slate com opacidade
          'bg-n-alpha-1 opacity-50': visualStatus === 'blocked',
          // Ready - transparente com hover
          'bg-n-alpha-1 hover:bg-n-alpha-2': visualStatus === 'ready',
        }"
      />

      <!-- Indicador de status (bolinha) -->
      <span
        class="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-n-solid-1 z-10"
        :class="{
          'bg-n-amber-9 animate-pulse':
            visualStatus === 'processing' || visualStatus === 'calling',
          'bg-n-ruby-9': visualStatus === 'disconnected',
          'bg-n-teal-9 animate-pulse': visualStatus === 'in-call',
          'bg-n-slate-8': visualStatus === 'blocked',
          'bg-n-teal-9': visualStatus === 'ready',
        }"
      />

      <!-- Spinner quando processando -->
      <template v-if="visualStatus === 'processing'">
        <svg
          class="relative z-10 h-4 w-4 animate-spin text-n-amber-11"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </template>

      <!-- Icone normal -->
      <template v-else>
        <span
          class="relative z-10 text-base"
          :class="[
            dynamicIcon,
            {
              'text-n-ruby-11': visualStatus === 'disconnected',
              'text-n-amber-11': visualStatus === 'calling',
              'text-white': visualStatus === 'in-call',
              'text-n-slate-9': visualStatus === 'blocked',
              'text-n-teal-11': visualStatus === 'ready',
            },
          ]"
        />
      </template>

      <!-- Label (opcional) -->
      <span
        v-if="label"
        class="relative z-10 ml-2 text-sm font-medium"
        :class="{
          'text-n-amber-11':
            visualStatus === 'processing' || visualStatus === 'calling',
          'text-n-ruby-11': visualStatus === 'disconnected',
          'text-white': visualStatus === 'in-call',
          'text-n-slate-9': visualStatus === 'blocked',
          'text-n-teal-11': visualStatus === 'ready',
        }"
      >
        {{ label }}
      </span>
    </button>
  </span>
</template>
